import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ownSpecialistId } from "../../../lib/own-specialist";
import { fallbackWeek } from "../../../screens/schedule/dates";
import { ScheduleSearch, scheduleWeekQuery } from "../../../screens/schedule/queries";
import { WeekView } from "../../../screens/schedule/week-view";

/**
 * The week grid. `prefetchQuery` (not `ensure`) so a failing read model still renders the
 * chrome — week navigation and the booking link anchor on a locally computed empty week,
 * exactly the degraded frame the Next page showed. A specialist's week is their own: the
 * filter is fixed to them (a `?specialist=` in the URL is ignored) and the picker is hidden.
 */
export const Route = createFileRoute("/_authed/schedule/")({
  validateSearch: ScheduleSearch,
  loaderDeps: ({ search }) => ({ week: search.week, specialist: search.specialist }),
  loader: ({ context, deps }) =>
    context.queryClient.prefetchQuery(
      scheduleWeekQuery(deps.week, ownSpecialistId(context.session) ?? deps.specialist),
    ),
  component: SchedulePage,
});

function SchedulePage() {
  const { week, specialist } = Route.useSearch();
  const { session } = Route.useRouteContext();
  const navigate = useNavigate();
  const locked = ownSpecialistId(session);
  const specialistId = locked ?? specialist;
  const { data, isError } = useQuery(scheduleWeekQuery(week, specialistId));
  const view = data ?? fallbackWeek(week, specialistId);

  function setSpecialist(next: string) {
    navigate({
      to: "/schedule",
      search: { ...(week ? { week } : {}), ...(next ? { specialist: next } : {}) },
    });
  }

  return (
    <WeekView
      view={view}
      dbError={isError}
      canPickSpecialist={!locked}
      onSpecialistChange={setSpecialist}
    />
  );
}
